import { Directive, HostListener, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';

/**
 * Directive that handles smart back navigation while preserving query parameters.
 *
 * When clicked, it will:
 * 1. Navigate back in browser history if there's a previous page within the same site (navigationId > 1)
 * 2. Fall back to the provided fallbackRoute if no in-site history exists
 *
 * The navigationId check ensures we only go back to pages within the same Angular application,
 * preventing navigation to external sites or direct URL entries. Query parameters are preserved
 * by the browser's history mechanism when using location.back().
 *
 * Supports both absolute routes (starting with '/') and relative routes (starting with './' or '../').
 * Relative routes are resolved using Angular's router.navigate() with relativeTo option.
 *
 * Usage:
 * ```html
 * <!-- Absolute route -->
 * <button appNavigationBack [fallbackRoute]="'/dashboard'">Back</button>
 *
 * <!-- Relative route -->
 * <button appNavigationBack [fallbackRoute]="'../dashboard'">Back</button>
 * ```
 */
@Directive({
  selector: '[appNavigationBack]',
})
export class BackNavigationDirective {
  @Input() fallbackRoute: string;

  constructor(private readonly location: Location, private readonly router: Router, private readonly activatedRoute: ActivatedRoute) {}

  @HostListener('click')
  onClick(): void {
    const state = this.location.getState() as { navigationId?: number };

    if (state?.navigationId && state.navigationId > 1) {
      this.location.back();
    } else {
      this.navigateToFallback();
    }
  }

  private navigateToFallback(): void {
    if (this.isRelativeRoute(this.fallbackRoute)) {
      this.router.navigate([this.fallbackRoute], { relativeTo: this.activatedRoute });
    } else {
      this.router.navigateByUrl(this.fallbackRoute);
    }
  }

  private isRelativeRoute(route: string): boolean {
    return route.startsWith('./') || route.startsWith('../');
  }
}
